import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  SelectChangeEvent,
  DialogTitle,
  CircularProgress,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { apiRemote, ICity } from "@/api/wp-api";
import { useNavigate } from "react-router";
import { useAlert } from "@/contexts/AlertContext";
import SelectWithSearch from "@/components/SelectWithSearch";
import {
  validatePassword,
  validateEmail,
  validateUsername,
  validateFirstnameLastname,
} from "@/utils/validation";
import {
  sanitizeInputUsername,
  sanitizeInputName,
  removeEmojiHtmlTag,
} from "@/utils/sanitizeInput";
import bgimg from "../images/bgimg.png";
import { ICountry, IRegisterForm } from "@/api";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import ReCAPTCHA from "react-google-recaptcha"; // Import ReCAPTCHA component

const generateYearOptions = (): string[] => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 1940; year <= currentYear; year++) {
    years.push(year.toString());
  }
  return years;
};

const RegisterForm = () => {
  const [cities, setCities] = useState<{ id: string; name: string }[]>([]);
  const [schools, setSchools] = useState<{ id: string; name: string }[]>([]);
  const navigate = useNavigate();
  const alert = useAlert();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [countries, setCountries] = useState<ICountry[]>([]);

  const [formData, setFormData] = useState<IRegisterForm>({
    firstname: "",
    lastname: "",
    username: "",
    password: "",
    confirmPassword: "",
    email: "",
    day: "",
    month: "",
    year: "",
    // country: "",
    // city: "",
    recaptchaToken: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const recaptchaRef = useRef(null);
  const RECAPTCHA_SITE_KEY = process.env.RECAPTCHA_SITE_KEY;

  useEffect(() => {
    const countryList = apiRemote
      .getCountryList()
      .then((data) => {
        setCountries(data);
      })
      .catch((err) => {
        console.log(err.message);
      });
  }, [countries]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    let sanitizedValue = value;

    if (name === "firstname" || name === "lastname") {
      sanitizedValue = sanitizeInputName(value);
      const nameError = validateFirstnameLastname(sanitizedValue);
      setErrors((prev) => ({
        ...prev,
        [name]: nameError,
      }));
    }

    if (name === "username") {
      sanitizedValue = sanitizeInputUsername(value);
      const userNameError = validateUsername(sanitizedValue);
      setErrors((prev) => ({
        ...prev,
        username: userNameError,
      }));
    }

    if (name === "password") {
      setErrors((prev) => ({
        ...prev,
        confirmPassword:
          formData.confirmPassword && value !== formData.confirmPassword
            ? "Passwords do not match"
            : "",
      }));
    }
    if (name === "confirmPassword") {
      setErrors((prev) => ({
        ...prev,
        confirmPassword:
          value !== formData.password ? "Passwords do not match" : "",
      }));
    }

    if (name === "email") {
      const emailError = validateEmail(value);
      setErrors((prev) => ({
        ...prev,
        email: emailError,
      }));
    }

    setFormData((prev) => ({
      ...prev,
      [name]: removeEmojiHtmlTag(sanitizedValue),
    }));
  };

  const fetchCities = async (countryId: string) => {
    try {
      const response = await apiRemote.getCityByCountry(countryId);
      if (response && Array.isArray(response)) {
        // Kiểm tra response có phải là mảng không
        setCities(
          response.map((city: ICity) => ({
            id: city.id.toString(),
            name: city.name,
          }))
        );
      } else {
        setCities([]); // Reset nếu không có dữ liệu
      }
    } catch (error) {
      console.error("Failed to fetch cities:", error);
      setCities([]); // Reset danh sách nếu xảy ra lỗi
    }
  };

  const handleSelectChange = (value: string, fieldName: string) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    if (fieldName === "country") {
      fetchCities(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Object.values(errors).some((error) => error) || Object.values(formData).some(value => value === "" && value !== formData.recaptchaToken)) {
      alert.error("Please fix the errors and fill all required fields before submitting.");
      return;
    }

    if (!formData.recaptchaToken) {
      alert.error("Please complete the reCAPTCHA verification.");
      return;
    }
    setIsSubmitting(true);
    apiRemote
      .registerUSer(formData)
      .then((data) => {
        if (data.code == 200) {
          alert.success(data.message);
          setIsRegistered(true);
          apiRemote.verifyEmail({
            email: formData.email,
            username: formData.username,
            firstname: formData.firstname,
            lastname: formData.lastname,
            password: formData.password
          }).then(()=>{
          }).catch((e)=>{
            console.log(e)
          })
        } else {
          alert.error(data.message);
        }
      })
      .catch((err) => {
        console.error(err.message);
      })
      .finally(() => {
        setIsSubmitting(false);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
          setFormData((prev) => ({ ...prev, recaptchaToken: "" }));
        }
      });
  };

  const handleClose = () => {
    navigate("..", { replace: true }); // Điều hướng mà không lưu lại trong lịch sử
    window.location.reload();
  };

  const days = Array.from({ length: 31 }, (_, i) => (i + 1).toString());
  const months = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
  const years = generateYearOptions();

  // Handle ReCAPTCHA token change
  const onRecaptchaChange = (token: string | null) => {
    setFormData((prev) => ({
      ...prev,
      recaptchaToken: token || "",
    }));
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `url(${bgimg}) no-repeat center center fixed`,
        backgroundSize: "cover",
        padding: 3,
      }}
    >
      {isRegistered ? (
        <Box
          sx={{
            maxWidth: 600,
            p: 4,
            bgcolor: "#000",
            borderRadius: 4,
            boxShadow: "0 0 0 2px #ff00ff",
            textAlign: "center",
            color: "#fff",
          }}
        >
          <Typography variant="h4" gutterBottom>
            Thank You for Registering!
          </Typography>
          <Typography variant="body1" paragraph>
            Thank you for joining Pythaverse! We're excited to have you as part
            of our community.
          </Typography>
          <Typography variant="body1" paragraph>
            Please check your email inbox (and spam/junk folder, just in case)
            for a confirmation message containing your account details. If you
            don't see the email within a few minutes, feel free to reach out to
            our support team at {" "}
            <a
              href="mailto:support@pythaverse.com"
              style={{
                color: "#00a0a0",
                textDecoration: "none",
                fontWeight: "bold",
              }}
            >
               support@pythaverse.com
            </a>
            .
          </Typography>
          <Typography variant="body1">
            Once you've verified your email, you can log in and start exploring
            all the amazing features Pythaverse has to offer.
          </Typography>
          <Typography variant="h6" sx={{ mt: 2 }}>
            Welcome aboard!
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            maxWidth: 600,
            margin: "0 auto",
            p: 4,
            bgcolor: "#000",
            borderRadius: 4,
            boxShadow: "0 0 0 2px #ff00ff",
            position: "relative",
            zIndex: 1,
            "& .MuiTypography-root": {
              color: "#ffffff",
            },
            "& .MuiInputBase-root": {
              bgcolor: "#ffffff",
              borderRadius: 2,
            },
            "& .MuiButton-containedPrimary": {
              bgcolor: "#00a0a0",
              color: "#ffffff",
              textTransform: "none",
              "&:hover": {
                bgcolor: "#008080",
              },
            },
            "& .MuiButton-outlinedSecondary": {
              color: "#ffffff",
              borderColor: "#ffffff",
              textTransform: "none",
              "&:hover": {
                borderColor: "#ff00ff",
              },
            },
            "& .MuiFormHelperText-root": {
              color: "#ff0000",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              color: "#ffffff !important",
              mb: 2,
              fontSize: "2.5rem",
            }}
          >
            {"Register Educator"}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <FormControl fullWidth margin="normal">
              <Box sx={{ display: "flex" }}>
                <Box sx={{ mr: 1, flex: 1 }}>
                  <Typography variant="body1" color="textSecondary">
                    First Name:{" "}
                    <span style={{ color: "red", fontWeight: "bold" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    margin="normal"
                    name="firstname"
                    sx={{ m: 0 }}
                    value={formData.firstname}
                    inputProps={{ maxLength: 50 }}
                    onChange={handleChange}
                    required
                    error={!!errors.firstname}
                    helperText={errors.firstname}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" color="textSecondary">
                    Last Name:{" "}
                    <span style={{ color: "red", fontWeight: "bold" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    margin="normal"
                    name="lastname"
                    sx={{ m: 0 }}
                    value={formData.lastname}
                    inputProps={{ maxLength: 50 }}
                    onChange={handleChange}
                    required
                    error={!!errors.lastname}
                    helperText={errors.lastname}
                  />
                </Box>
              </Box>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Typography variant="body1" color="textSecondary">
                Username:{" "}
                <span style={{ color: "red", fontWeight: "bold" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                margin="normal"
                name="username"
                sx={{ m: 0 }}
                value={formData.username}
                inputProps={{ maxLength: 20 }}
                onChange={handleChange}
                required
                error={!!errors.username}
                helperText={errors.username}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Typography variant="body1" color="textSecondary">
                Password:{" "}
                <span style={{ color: "red", fontWeight: "bold" }}>*</span>
              </Typography>
              {/* <TextField
                fullWidth
                margin="normal"
                name="password"
                sx={{ m: 0 }}
                value={formData.password}
                onChange={handleChange}
                required
                type="password"
                error={!!errors.password}
                helperText={errors.password}
              /> */}
              <TextField
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Typography>Confirm Password:{" "}
              <span style={{ color: "red", fontWeight: "bold" }}>*</span>
              </Typography>
              <TextField
                name="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Typography variant="body1" color="textSecondary">
                Email:{" "}
                <span style={{ color: "red", fontWeight: "bold" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                margin="normal"
                name="email"
                sx={{ m: 0 }}
                value={formData.email}
                onChange={handleChange}
                required
                type="email"
                error={!!errors.email}
                helperText={errors.email}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Typography variant="body1" color="textSecondary">
                Date of Birth:{" "}
                <span style={{ color: "red", fontWeight: "bold" }}>*</span>
              </Typography>
              <Box sx={{ display: "flex" }}>
                <Box sx={{ mr: 1, flex: 1 }}>
                  <Typography variant="body1" color="textSecondary">
                    Day{" "}
                    <span style={{ color: "red", fontWeight: "bold" }}>*</span>
                  </Typography>
                  <SelectWithSearch
                    options={days.map((item) => ({
                      value: item,
                      label: item,
                    }))}
                    label={"Day"}
                    value={formData.day || ""}
                    required={true}
                    disabled={false}
                    onChange={(value) => {
                      handleSelectChange(value.value, "day");
                    }}
                  />
                </Box>
                <Box sx={{ mr: 1, flex: 1 }}>
                  <Typography variant="body1" color="textSecondary">
                    Month:{" "}
                    <span style={{ color: "red", fontWeight: "bold" }}>*</span>
                  </Typography>
                  <SelectWithSearch
                    options={months.map((item) => ({
                      value: item,
                      label: item,
                    }))}
                    label={"Month"}
                    value={formData.month || ""}
                    required={true}
                    disabled={false}
                    onChange={(value) => {
                      handleSelectChange(value.value, "month");
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" color="textSecondary">
                    Year:{" "}
                    <span style={{ color: "red", fontWeight: "bold" }}>*</span>
                  </Typography>
                  <SelectWithSearch
                    options={years.map((item) => ({
                      value: item,
                      label: item,
                    }))}
                    label={"Year"}
                    value={formData.year || ""}
                    required={true}
                    disabled={false}
                    onChange={(value) => {
                      handleSelectChange(value.value, "year");
                    }}
                  />
                </Box>
              </Box>
            </FormControl>

            {/* <FormControl fullWidth margin="normal">
              <Typography variant="body1" color="textSecondary">
                Country:{" "}
                <span style={{ color: "red", fontWeight: "bold" }}>*</span>
              </Typography>
              <SelectWithSearch
                options={countries.map((item) => ({
                  value: item.id.toString(),
                  label: item.country_name,
                }))}
                label={"Country"}
                value={formData.country || ""}
                required={true}
                disabled={false}
                onChange={(value) => {
                  handleSelectChange(value.value, "country");
                }}
              />
            </FormControl> */}

            {/* <FormControl fullWidth margin="normal">
              <Typography variant="body1" color="textSecondary">
                City:{" "}
                <span style={{ color: "red", fontWeight: "bold" }}>*</span>
              </Typography>
              <SelectWithSearch
                options={cities.map((item) => ({
                  value: item.id.toString(),
                  label: item.name,
                }))}
                label={"City"}
                value={formData.city || ""}
                required={true}
                disabled={false}
                onChange={(value) => {
                  handleSelectChange(value.value, "city");
                }}
              />
            </FormControl> */}

             {/* ReCAPTCHA component */}
             <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <ReCAPTCHA
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={onRecaptchaChange}
                ref={recaptchaRef}
              />
            </Box>

            <Box
              sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}
            >
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ mr: 2 }}
                disabled={isSubmitting || !formData.recaptchaToken} // Vô hiệu hóa khi đang xử lý
              >
                Submit
              </Button>
            </Box>
          </form>
        </Box>
      )}
    </Box>
  );
};

export default RegisterForm;
